import { SetStateAction } from "react";
import { ModalWithButtons } from "./modal";
import { BlueButton } from "../button";
import { useTranslation } from "react-i18next";
import { NeutralColor } from "node-library/dist/colors";
import { useState } from "react";
import { CustomTextInput } from "../inputs";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { useSnackbar } from "../toast/showToastMessage";
import { Severity } from "node-library/dist/constants";
import { GreenTickIcon } from "../toast/successIcons";
import { RedErrorIcon } from "../toast/errorIcons";
import { set } from "date-fns";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<SetStateAction<boolean>>;
	proceedAction: () => void;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
	goBackToViewOrganizationsPage: (refresh: boolean) => void;
}

export default function NewOrganizationModal(props: Props) {
	const { t: translation } = useTranslation();

	const [clientCompanyName, setClientCompanyName] = useState<string>("");
	const [address, setAddress] = useState<string>("");
	const [city, setCity] = useState<string>("");
	const [province, setProvince] = useState<string>("");
	const [postalCode, setPostalCode] = useState<string>("");
	const [country, setCountry] = useState<string>("");

	const [clientCompanyNameErrorMsg, setClientCompanyNameErrorMsg] =
		useState<string>("");

	const [addressErrorMsg, setAddressErrorMsg] = useState<string>("");
	const [cityErrorMsg, setCityErrorMsg] = useState<string>("");
	const [postalCodeErrorMsg, setPostalCodeErrorMsg] = useState<string>("");
	const [countryErrorMsg, setCountryErrorMsg] = useState<string>("");
	const [provinceErrorMsg, setProvinceErrorMsg] = useState<string>("");
	const { openSnackbar } = useSnackbar();

	const { setUnsavedChanges, goBackToViewOrganizationsPage } = props;

	const organizationInformationFields1: Array<any> = [
		{
			id: "organization-company-name-input",
			label: `${translation("CompanyName")}`,
			inputType: "text",
			value: clientCompanyName,
			setValue: setClientCompanyName,
			required: true,
			errorMsg: clientCompanyNameErrorMsg,
			setErrorMsg: setClientCompanyNameErrorMsg,
		},
	];

	const organizationInformationFields2: Array<any> = [
		{
			id: "organization-address-input",
			label: `${translation("Address")}`,
			inputType: "text",
			value: address,
			setValue: setAddress,
			required: true,
			errorMsg: addressErrorMsg,
			setErrorMsg: setAddressErrorMsg,
		},
	];
	const organizationInformationFields3: Array<any> = [
		{
			id: "organization-city-input",
			label: `${translation("City")}`,
			inputType: "text",
			value: city,
			setValue: setCity,
			errorMsg: cityErrorMsg,
			setErrorMsg: setCityErrorMsg,
		},
		{
			id: "organization-postal-code-input",
			label: `${translation("PostalCodeOrZip")}`,
			inputType: "text",
			value: postalCode,
			setValue: setPostalCode,
			errorMsg: postalCodeErrorMsg,
			setErrorMsg: setPostalCodeErrorMsg,
		},
	];
	const organizationInformationFields4: Array<any> = [
		{
			id: "organization-country-input",
			label: `${translation("Country")}`,
			inputType: "text",
			value: country,
			setValue: setCountry,
			required: true,
			errorMsg: countryErrorMsg,
			setErrorMsg: setCountryErrorMsg,
		},
		{
			id: "organization-province-input",
			label: `${translation("ProvinceOrState")}`,
			inputType: "text",
			value: province,
			setValue: setProvince,
			errorMsg: provinceErrorMsg,
			setErrorMsg: setProvinceErrorMsg,
		},
	];

	async function handleSave() {
		let hasError: boolean = false;

		// Simple validation for required fields
		const allFields = [
			...organizationInformationFields1,
			...organizationInformationFields2,
			...organizationInformationFields3,
			...organizationInformationFields4,
		];

		for (const field of allFields) {
			if (
				field.required &&
				(!field.value || (field.value as string).trim().length === 0)
			) {
				field.setErrorMsg(translation("MissingInformation"));
				hasError = true;
			} else {
				field.setErrorMsg("");
			}
		}

		if (hasError) {
			return;
		}
		try {
			const response = await sendPOSTRequest(
				"/organizations/admin-create-organization",
				{
					clientCompanyName: clientCompanyName,
					address: address,
					city: city,
					province: province,
					country: country,
					postalCode: postalCode,
				}
			);

			openSnackbar(
				translation("OrganizationCreated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);

			setUnsavedChanges(false);
			clearFieldsAfterSubmission();
			props.setOpen(false);
			await goBackToViewOrganizationsPage(true);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	function clearFieldsAfterSubmission() {
		setClientCompanyName("");
		setAddress("");
		setCity("");
		setProvince("");
		setPostalCode("");
		setCountry("");
	}

	const modal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("AddOrganization")}
			open={props.open}
			setOpen={props.setOpen}
			buttons={[
				<div
					key="add-organization-save-button"
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						margin: "0px 40px",
					}}
				>
					<BlueButton
						buttonClassName="add-organization-save-button"
						id="add-organization-save-button"
						children={translation("Save")}
						size="flex"
						onClick={() => {
							handleSave();
						}}
					></BlueButton>
				</div>,
			]}
		>
			<div
				className="typography-caption"
				style={{
					color: NeutralColor.NEUTRAL_GREY_500,
					maxWidth: "70%",
					marginTop: "-30px",
					marginBottom: "40px",
				}}
			>
				{translation("AddOrganizationDescription")}
			</div>
			<div
				style={{
					marginLeft: "5px",
					marginRight: "5px",
					marginBottom: "40px",
				}}
			>
				<div
					className="organization-info-input-row-1"
					style={{
						marginBottom: "15px",
					}}
				>
					{organizationInformationFields1.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
				<div
					className="organization-info-input-row-1"
					style={{
						marginBottom: "15px",
					}}
				>
					{organizationInformationFields2.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
				<div
					className="organization-info-input-row-2"
					style={{
						marginBottom: "15px",
					}}
				>
					{organizationInformationFields3.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
				<div className="organization-info-input-row-2">
					{organizationInformationFields4.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
			</div>
		</ModalWithButtons>
	);

	return modal;
}
